import React from "react";
import { Box } from "@mui/material";
import "./LogoLoader.css";

const SIZE_MAP = {
  sm: { box: 36, stroke: 3.5 },
  md: { box: 72, stroke: 5 },
  lg: { box: 112, stroke: 6 },
};

const RADIUS = 44;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const PULSE_DURATION = "2s";

const LogoLoader = ({ size = "md", progress, duration = "1.2s", fullPage = false }) => {
  const { box, stroke } = SIZE_MAP[size] || SIZE_MAP.md;
  const isDeterminate = typeof progress === "number";
  const clampedProgress = Math.min(Math.max(progress || 0, 0), 100);
  const offset = CIRCUMFERENCE * (1 - clampedProgress / 100);
  const inkColor = fullPage ? "#F5F8FB" : "#12243C"; //dot lining
  const accentColor = fullPage ? "#4FA9CF" : "#0073A6"; //dots color

  const loader = (
    <Box
      sx={{
        position: "relative",
        width: box,
        height: box,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          transform: "rotate(-90deg)",
        }}
      >
        <defs>
          <linearGradient id="logoLoaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4FA9CF" />
            <stop offset="100%" stopColor="#0073A6" />
          </linearGradient>
        </defs>
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke={fullPage ? "rgba(245, 248, 251, 0.30)" : "rgba(18, 36, 60, 0.10)"}
          strokeWidth={stroke}
        />
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke="url(#logoLoaderGradient)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={
            isDeterminate
              ? CIRCUMFERENCE
              : `${CIRCUMFERENCE * 0.75} ${CIRCUMFERENCE * 0.25}`
          }
          strokeDashoffset={isDeterminate ? offset : 0}
          className={isDeterminate ? undefined : "logoLoaderArc"}
          style={
            isDeterminate
              ? {
                  transformOrigin: "50% 50%",
                  transition: "stroke-dashoffset 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }
              : { animationDuration: duration }
          }
        />
      </Box>

      <Box
        sx={{ width: "46%", aspectRatio: "1", display: "grid", placeItems: "center" }}
        className={isDeterminate ? undefined : "logoLoaderMarkPulse"}
        style={isDeterminate ? undefined : { animationDuration: PULSE_DURATION }}
      >
        <svg viewBox="0 0 16 16" width="100%" height="100%">
          <polygon
            fill="none"
            stroke={inkColor}
            strokeLinejoin="round"
            strokeWidth="1.19"
            points="1.36 2.14 1.36 10.03 8 14.04 8 6.09 1.36 2.14"
          />
          <polygon
            fill="none"
            stroke={inkColor}
            strokeLinejoin="round"
            strokeWidth="1.19"
            points="14.63 2.14 14.63 10.03 8 14.04 8 6.09 14.63 2.14"
          />
          <circle fill={accentColor} cx="1.36" cy="2.36" r="1.36" />
          <circle fill={inkColor} cx="1.36" cy="10.08" r="1.36" />
          <circle fill={accentColor} cx="14.63" cy="2.36" r="1.36" />
          <circle fill={inkColor} cx="14.63" cy="10.08" r="1.36" />
          <circle fill={accentColor} cx="8.01" cy="5.82" r="1.36" />
          <circle fill={inkColor} cx="8.01" cy="13.64" r="1.36" />
        </svg>
      </Box>
    </Box>
  );

  if (!fullPage) return loader;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      {loader}
    </Box>
  );
};

export default LogoLoader;
